import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Container, Row, Col, Carousel, Card, Alert, Button, Table, Form } from 'react-bootstrap';
import { useAuthState } from 'react-firebase-hooks/auth';
import LoginModal from '../auth/LoginModal';  // Correct path to LoginModal
import { sendEmail } from '../util_code/emailService'; // Import the email service
import './viewad.css';

// Remove API key from client-side code
// const MAILGUN_SENDING_API_KEY = process.env.REACT_APP_MAILGUN_SENDING_API_KEY;

// MessageForm component
const MessageForm = ({ userName, onSubmit, message, setMessage, user }) => {
  return (
    <Card className="mt-3">
      <Card.Body>
        <h3 className='message-title'>Message {userName}</h3>
        {!user ? (
          <Alert variant="info">
            Please <Link to="/login">login</Link> to send messages
          </Alert>
        ) : (
          <Form onSubmit={onSubmit}>
            <div className="form-wrapper">
              <Form.Group className="message-input">
                <Form.Control
                  as="textarea"
                  rows={3}
                  style={{ resize: 'vertical', minHeight: '60px' }}
                  placeholder="Enter your message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </Form.Group>
              <div className="button-container">
                <Button 
                  variant="primary" 
                  type="submit" 
                  className='send-message-button'
                >
                  Send
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

const ViewAd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [ad, setAd] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [sellerEmail, setSellerEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [message, setMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const fetchAdAndSellerInfo = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        // Check if user is logged in
        if (!user) {
          setShowLoginModal(true);
          setLoading(false);
          return;
        }

        console.log('Current user details:', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        });

        // Fetch ad data
        const adDoc = await getDoc(doc(db, 'ads', id));
        if (adDoc.exists()) {
          const adData = { id: adDoc.id, ...adDoc.data() };
          setAd(adData);
          console.log('Ad data loaded:', adData);

          // Fetch seller info
          if (adData.userId) {
            const sellerDoc = await getDoc(doc(db, 'users', adData.userId));
            
            if (sellerDoc.exists()) {
              const sellerData = sellerDoc.data();
              setSellerInfo(sellerData);
              
              // Specifically extract and store email
              if (sellerData.email) {
                setSellerEmail(sellerData.email);
                console.log('Seller email found and stored:', sellerData.email);
              } else {
                console.log('No email found in seller data');
                
                // Try to get email from ad object as fallback
                if (adData.email) {
                  setSellerEmail(adData.email);
                  console.log('Using email from ad as fallback:', adData.email);
                } else {
                  setSellerEmail('fallback@example.com');
                  console.log('No email found, using default fallback');
                }
              }
              
              console.log('Seller info loaded:', {
                ...sellerData,
                email: sellerData.email || 'Not provided' 
              });
            } else {
              console.log('Seller document does not exist');
              if (adData.email) {
                setSellerEmail(adData.email);
                console.log('Using email from ad as fallback:', adData.email);
              }
            }
          } else {
            console.log('No seller ID in ad data');
            // Try to use ad.email as fallback
            if (adData.email) {
              setSellerEmail(adData.email);
              console.log('Using email from ad without seller ID:', adData.email);
            }
          }
        } else {
          setError('Ad not found');
          console.log('Ad document does not exist');
        }
      } catch (err) {
        console.error('Error fetching ad and seller info:', err);
        setError('Error loading ad details');
      }
      setLoading(false);
    };

    fetchAdAndSellerInfo();
  }, [id, user]);

  const handleShowPhoneNumber = () => {
    if (!user) {
      setShowLoginModal(true);  // Show login modal instead of setting error
      return;
    }
    setShowPhoneNumber(true);
  };

  const handleMessage = () => {
    if (!user) {
      setShowLoginModal(true);  // Show login modal instead of setting error
      return;
    }
    navigate(`/messages`);
  };

  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      // Check if a conversation already exists between these users for this ad
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('adId', '==', id),
        where('participants', 'array-contains', user.uid)
      );
      const querySnapshot = await getDocs(q);
      
      let conversationId;
      
      if (querySnapshot.empty) {
        // Create new conversation
        const conversationDoc = await addDoc(conversationsRef, {
          adId: id,
          adTitle: ad.ItemName,
          participants: [user.uid, ad.userId],
          lastMessage: message,
          lastMessageTime: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        conversationId = conversationDoc.id;
      } else {
        conversationId = querySnapshot.docs[0].id;
      }

      // Add the message to the conversation
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      await addDoc(messagesRef, {
        text: message,
        senderId: user.uid,
        senderName: user.displayName,
        timestamp: serverTimestamp()
      });

      // Update the conversation's last message
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: message,
        lastMessageTime: serverTimestamp()
      });

      // Use a server-side function for email sending instead of directly using API key
      try {
        // Prepare email data
        const emailData = {
          recipientEmail: sellerEmail || 'joelhieber1@gmail.com',
          subject: 'New Message on Totly',
          senderName: user.displayName || 'Unknown sender',
          receiverName: ad.userName || 'User',
          messageText: message,
          adTitle: ad.ItemName,
          adId: id
        };
        
        console.log('Preparing to send email notification');
        
        // Option 1: Direct Firebase Function call (preferred method)
        try {
          const result = await sendEmail(emailData);
          console.log('Email notification sent successfully via Firebase:', result);
        } catch (firebaseError) {
          console.error('Error sending email via Firebase:', firebaseError);
          
          // Fallback to API route if Firebase direct call fails
          console.log('Falling back to API route');
          const response = await fetch('/api/send-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailData)
          });
          
          if (response.ok) {
            console.log('Email notification request sent successfully via API');
          } else {
            console.error('Email notification request failed:', await response.text());
          }
        }
      }
      catch (emailError) {
        console.error('Error requesting email notification:', emailError);
        // Don't let email errors affect the main flow
      }

      setMessage('');
      // Navigate to messages
      navigate('/messages');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Error sending message. Please try again.');
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // Optionally refresh the ad data here if needed
  };

  if (loading) return <Container><Alert variant="info">Loading...</Alert></Container>;
  if (!user) {
    return (
      <Container>
        <Alert variant="info">
          Please log in to view this ad
        </Alert>
        <LoginModal 
          show={showLoginModal}
          onHide={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    );
  }
  if (error) return <Container><Alert variant="warning">{error}</Alert></Container>;
  if (!ad) return <Container><Alert variant="warning">Ad not found</Alert></Container>;

  return (
    <Container className="mt-4">
      <Card className="ad-details-card">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Carousel className="ad-carousel">
                {ad.imageUrls && ad.imageUrls.length > 0 ? (
                  ad.imageUrls.map((url, index) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100"
                        src={url}
                        alt={`Slide ${index + 1}`}
                      />
                    </Carousel.Item>
                  ))
                ) : ad.imageUrl ? (
                  <Carousel.Item>
                    <img
                      className="d-block w-100"
                      src={ad.imageUrl}
                      alt="Ad"
                    />
                  </Carousel.Item>
                ) : (
                  <Alert variant="warning">No images available</Alert>
                )}
              </Carousel>
            </Col>
            <Col md={6}>
              <h2 className="ad-title">{ad.ItemName}</h2>
              <Table className="ad-details-table">
                <tbody>
                  {ad.category && <tr><th>Category</th><td>{ad.category}</td></tr>}
                  {ad.subcategory && <tr><th>Subcategory</th><td>{ad.subcategory}</td></tr>}
                  {ad.brand && <tr><th>Brand</th><td>{ad.brand}</td></tr>}
                  {ad.YearModel && <tr><th>Year</th><td>{ad.YearModel}</td></tr>}
                  {ad.engineOption && <tr><th>Engine Type</th><td>{ad.engineOption}</td></tr>}
                  {ad.engineSize && <tr><th>Engine Size</th><td>{ad.engineSize}</td></tr>}
                  {ad.condition && <tr><th>Condition</th><td>{ad.condition}</td></tr>}
                  {ad.Price && <tr><th>Price</th><td>R{formatPrice(ad.Price)}</td></tr>}
                  {ad.description && <tr><th>Description</th><td>{ad.description}</td></tr>}
                  {ad.province && (
                    <tr>
                      <th>Location</th>
                      <td>{ad.city ? `${ad.city}, ${ad.province}` : ad.province}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Posted by</th>
                    <td>
                      {ad.userId ? (
                        <Link to={`/view-profile/${ad.userId}`}>
                          {ad.userName}
                        </Link>
                      ) : (
                        ad.userName
                      )}
                    </td>
                  </tr>
                  {ad.createdAt && (
                    <tr>
                      <th>Posted on</th>
                      <td>{ad.createdAt.toDate().toLocaleString()}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div className="mt-3">
                {!showPhoneNumber ? (
                  <Button 
                    variant="primary" 
                    onClick={handleShowPhoneNumber}
                    className="phone_button"
                  >
                    Show Phone Number
                  </Button>
                ) : (
                  <p><strong>Phone Number:</strong> {sellerInfo?.phoneNumber || 'Not provided'}</p>
                )}
                <Button 
                  variant="success" 
                  onClick={handleMessage}
                  className="message_button"
                >
                  Go to Messages
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <div>
        <MessageForm 
          userName={ad.userName}
          onSubmit={handleSubmitMessage}
          message={message}
          setMessage={setMessage}
          user={user}
        />
      </div>
      <div className="mt-3 d-flex justify-content-between">
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </div>
      <div className='other-ads'>
        <h3>Other Ads</h3>
        {/* Display other ads here */}
      </div>

      {/* Add the LoginModal component */}
      <LoginModal 
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Container>
  );
};

export default ViewAd;